import type {
  Dispatch,
  MutableRefObject,
  PropsWithChildren,
  SetStateAction,
} from 'react'
import { createContext, useContext, useRef, useState } from 'react'

import { useLocalStorage } from '@Hooks/useStorage'

type FastFetchModalProps = {
  isOpen: boolean
  close: Function
  open: Function
  trackerId?: number | undefined
  setTrackerId: Dispatch<SetStateAction<number | undefined>>
  competitorName?: string | undefined
  setCompetitorName: Dispatch<SetStateAction<string | undefined>>
  isMine: boolean
  setIsMine: Dispatch<SetStateAction<boolean>>
  doNotShow: boolean
  setDoNotShow: Dispatch<SetStateAction<boolean>>
  onCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  savedScrollY: MutableRefObject<number>
}

const FastFetchModalContext = createContext<FastFetchModalProps | null>(null)

export function useFastFetchModal() {
  const state = useContext(FastFetchModalContext)

  if (!state) {
    throw new Error(
      'useFastFetchModal must be used within FastFetchModalProvider.',
    )
  }

  return state
}

export function FastFetchModalProvider({ children }: PropsWithChildren) {
  const localStorage = useLocalStorage()

  const savedScrollY = useRef(0)
  const [isOpen, setIsOpen] = useState(false)
  const [doNotShow, setDoNotShow] = useState(
    localStorage.getSafe('urlFastFetchDoNotShowAgain')?.value === 'true',
  )
  const [isMine, setIsMine] = useState(false)
  const [trackerId, setTrackerId] = useState<number | undefined>()
  const [competitorName, setCompetitorName] = useState<string | undefined>()
  const close = () => setIsOpen(false)
  const open = () => {
    savedScrollY.current = window.scrollY
    setIsOpen(true)
  }

  const onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked

    localStorage.save('urlFastFetchDoNotShowAgain', isChecked.toString())
    setDoNotShow(isChecked)
  }

  return (
    <FastFetchModalContext.Provider
      value={{
        isOpen,
        close,
        open,
        trackerId,
        setTrackerId,
        competitorName,
        setCompetitorName,
        isMine,
        setIsMine,
        setDoNotShow,
        doNotShow,
        onCheckboxChange,
        savedScrollY,
      }}
    >
      {children}
    </FastFetchModalContext.Provider>
  )
}
